<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"

// ================================
// TYPES & INTERFACES
// ================================
/** Variante d’affichage du toggle « consigne nuit » (grilles legacy). */
type NightToggleVariant = "uniform" | "week" | "weekend"

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Désactive les trois champs. */
        disabled?: boolean
        /** Message d’erreur vee-validate (consigne). */
        errorConsigne?: string
        /** Message d’erreur vee-validate (limite haute). */
        errorLimhaute?: string
        /** Message d’erreur vee-validate (limite basse). */
        errorLimbasse?: string
        /** id accessibilité — consigne. */
        inputIdConsigne: string
        /** id accessibilité — limite haute. */
        inputIdLimhaute: string
        /** id accessibilité — limite basse. */
        inputIdLimbasse: string
        /** Libellé consigne. */
        /** Libellé consigne (défaut : clé i18n). */
        labelConsigne?: string
        /** Libellé limite haute (défaut : clé i18n). */
        labelLimhaute?: string
        /** Libellé limite basse (défaut : clé i18n). */
        labelLimbasse?: string
        /** Affiche la colonne « Consigne différente la nuit ? » (série horaire). */
        showNightToggle?: boolean
        /** Présentation du libellé / bloc toggle nuit. */
        nightToggleVariant?: NightToggleVariant
        /** Unité métier (`ConfigVariable.unit`) affichée en suffixe de chaque champ. */
        variableUnit?: string
    }>(),
    {
        disabled: false,
        errorConsigne: undefined,
        errorLimhaute: undefined,
        errorLimbasse: undefined,
        labelConsigne: undefined,
        labelLimhaute: undefined,
        labelLimbasse: undefined,
        showNightToggle: false,
        nightToggleVariant: "uniform",
        variableUnit: "",
    }
)

const consigne = defineModel<number>("consigne", { required: true })
const limhaute = defineModel<number>("limhaute", { required: true })
const limbasse = defineModel<number>("limbasse", { required: true })
const isNight = defineModel<boolean>("isNight", { default: false })

const emit = defineEmits<{
    /** Émis après toute modification d’un des trois champs (pas sur le toggle nuit). */
    change: []
}>()

const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                yes: "Oui",
                no: "Non",
                labelConsigne: "Consigne",
                labelLimHaute: "Limite haute",
                labelLimBasse: "Limite basse",
                differentNightConsigne: "Consigne différente la nuit ?",
            },
        },
    },
})

const getDistinguishBooleanOptions = computed(() => [
    { label: t("alarm.yes"), value: true },
    { label: t("alarm.no"), value: false },
])

const getLabelConsigne = computed(
    () => props.labelConsigne ?? t("alarm.labelConsigne")
)
const getLabelLimhaute = computed(
    () => props.labelLimhaute ?? t("alarm.labelLimHaute")
)
const getLabelLimbasse = computed(
    () => props.labelLimbasse ?? t("alarm.labelLimBasse")
)

// ================================
// COMPUTED
// ================================
const getNightToggleLayout = computed(() => {
    const wrapperBase = "flex min-w-0 shrink-0 flex-col items-center gap-2"
    if (props.nightToggleVariant === "uniform") {
        return {
            wrapper: wrapperBase,
            label: "text-center text-nowrap text-base font-semibold text-primary",
        }
    }
    if (props.nightToggleVariant === "week") {
        return {
            wrapper: wrapperBase,
            label: "text-center text-sm font-semibold text-primary",
        }
    }
    return {
        wrapper: wrapperBase,
        label: "text-center text-nowrap text-sm font-semibold text-primary",
    }
})

// ================================
// INTERACTIONS
// ================================
/**
 * Arrondit un nombre à 2 décimales (seuils en float, pas entiers).
 *
 * @param val valeur brute du contrôle
 */
function roundToTwoDecimals(val: unknown): number {
    const n = Number(val ?? 0)
    return Math.round(n * 100) / 100
}

/**
 * Met à jour la consigne numérique et notifie le parent.
 *
 * @param val valeur brute du contrôle
 */
function onConsigneChange(val: unknown) {
    consigne.value = roundToTwoDecimals(val)
    emit("change")
}

/**
 * Met à jour la limite haute et notifie le parent.
 *
 * @param val valeur brute du contrôle
 */
function onLimhauteChange(val: unknown) {
    limhaute.value = roundToTwoDecimals(val)
    emit("change")
}

/**
 * Met à jour la limite basse et notifie le parent.
 *
 * @param val valeur brute du contrôle
 */
function onLimbasseChange(val: unknown) {
    limbasse.value = roundToTwoDecimals(val)
    emit("change")
}
</script>

<template>
    <BaseInputWrapper
        :label="getLabelConsigne"
        :input-id="props.inputIdConsigne"
        :suffix="props.variableUnit"
        :error="props.errorConsigne ?? ''"
    >
        <BaseInputNumber
            :model-value="consigne"
            mode="decimal"
            locale="fr-FR"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :step="0.01"
            :disabled="props.disabled"
            :invalid="Boolean(props.errorConsigne)"
            fluid
            @update:model-value="onConsigneChange"
        />
    </BaseInputWrapper>
    <BaseInputWrapper
        :label="getLabelLimhaute"
        :input-id="props.inputIdLimhaute"
        :suffix="props.variableUnit"
        :error="props.errorLimhaute ?? ''"
    >
        <BaseInputNumber
            :model-value="limhaute"
            mode="decimal"
            locale="fr-FR"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :step="0.01"
            :disabled="props.disabled"
            :invalid="Boolean(props.errorLimhaute)"
            fluid
            @update:model-value="onLimhauteChange"
        />
    </BaseInputWrapper>
    <BaseInputWrapper
        :label="getLabelLimbasse"
        :input-id="props.inputIdLimbasse"
        :suffix="props.variableUnit"
        :error="props.errorLimbasse ?? ''"
    >
        <BaseInputNumber
            :model-value="limbasse"
            mode="decimal"
            locale="fr-FR"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :step="0.01"
            :disabled="props.disabled"
            :invalid="Boolean(props.errorLimbasse)"
            fluid
            @update:model-value="onLimbasseChange"
        />
    </BaseInputWrapper>
    <div v-if="props.showNightToggle" :class="getNightToggleLayout.wrapper">
        <span :class="getNightToggleLayout.label">
            {{ t("alarm.differentNightConsigne") }}
        </span>
        <div class="flex w-1/4 justify-center">
            <BaseSelectButton
                v-model="isNight"
                :options="getDistinguishBooleanOptions"
                option-label="label"
                option-value="value"
                :disabled="props.disabled"
            />
        </div>
    </div>
</template>
